// K11: 通用"可放大"媒体容器 —— HTML iframe / 图片 / 任何嵌入式预览复用
//
// 默认 inline 显示, 受 maxHeight 限制; 点 ⛶ -> 全屏覆盖层 (backdrop 点击 / Esc 关)
//
// 跟 TerminalBlock expanded 风格一致 (macOS 风 titlebar + 圆点按钮)

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface ExpandableMediaProps {
  /** 标题栏文本 (左) */
  title: React.ReactNode;
  /** 标题栏右侧自定义按钮 (打开 / 下载等) */
  actions?: React.ReactNode;
  /** 内嵌内容 (iframe / img / ...) */
  children: React.ReactNode;
  /** 内联高度 (px), 放大后忽略 */
  inlineHeight?: number;
  /** 容器额外 className */
  className?: string;
}

export function ExpandableMedia({
  title,
  actions,
  children,
  inlineHeight = 360,
  className = "",
}: ExpandableMediaProps) {
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Esc 关闭 + 锁页面滚动
  useEffect(() => {
    if (!expanded) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setExpanded(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey, true);
    };
  }, [expanded]);

  // K11 修: 父级若有 transform / filter / will-change 等会创建新的 containing
  // block, 让 position:fixed 相对该父级而非 viewport 算, 导致 overlay 错位 /
  // 溢出屏幕。用 Portal 把 expanded overlay 渲染到 document.body 下面绕开。
  const inner = (
    <div
      ref={rootRef}
      className={`exp-media${expanded ? " is-expanded" : ""} ${className}`.trim()}
    >
      <header className="exp-media__head">
        <span className="exp-media__title">{title}</span>
        <span className="exp-media__actions">
          {actions}
          <button
            type="button"
            className="exp-media__zoom"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            title={expanded ? "收起 (Esc)" : "放大"}
            aria-label={expanded ? "收起" : "放大"}
          >
            {expanded ? "✕" : "⛶"}
          </button>
        </span>
      </header>
      <div
        className="exp-media__body"
        style={{ height: expanded ? undefined : inlineHeight }}
      >
        {children}
      </div>
    </div>
  );

  if (expanded) {
    return createPortal(
      <>
        <div className="exp-media__backdrop" onClick={() => setExpanded(false)} />
        {inner}
      </>,
      document.body,
    );
  }
  return inner;
}
